import { Grid, Typography, Button, Menu, TextField, MenuItem } from '@mui/material';
import { gridSpacing } from 'store/constant';
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend } from 'recharts'; // Importing from recharts
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import { getApi } from 'views/services/api';
import { Box, Container } from '@mui/system';
import { saveAs } from 'file-saver';
import ExpensesChart from './ExpensesChart';
import ReservedRoomView from './Default/ReservedRoomsTable';
import { Stack } from '@mui/system';
import { currencies } from 'utils/password-strength';

// ==============================|| DEFAULT REPORTS ||============================== //

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  textAlign: 'left',
  color: theme.palette.text.secondary
}));

const GeneralReports = () => {
  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const [reservationData, setReservationData] = useState([]);
  const [separateFoodData, setSeparateFoodData] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper function to check if a date is within the specified range
  const isDateInRange = (date, fromDate, toDate) => {
    const checkDate = new Date(date);
    const start = new Date(fromDate);
    const end = new Date(toDate);
    return checkDate >= start && checkDate <= end;
  };

  const fetchReservationData = async () => {
    try {
      const response = await getApi(`api/reservation/viewallcompletedreservations/${hotel.hotelId}`);
      let filteredData = response.data.reservationData;
      console.log(filteredData, 'all reservation data');  

      if (fromDate && toDate) {
        filteredData = filteredData.filter((item) =>
          isDateInRange(item.checkOutDate, fromDate, toDate)
        );
      }

      setReservationData(filteredData);
    } catch (error) {
      console.error('Error fetching reservation data:', error);
    }
  };

  const fetchBillData = async () => {
    try {
      const response = await getApi(`api/separatefoodinvoice/view/${hotel.hotelId}`);
      const invoiceData = response?.data?.InvoiceData || [];

      const filteredData = invoiceData.filter((item) => {
        const itemDate = new Date(item.createdDate);
        return fromDate && toDate ? isDateInRange(item.createdDate, fromDate, toDate) : true;
      });

      setSeparateFoodData(filteredData);
    } catch (error) {
      console.log(error);
    }
  };

  // Set today's date as default for both fromDate and toDate when the component mounts
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]; // Format date to 'YYYY-MM-DD'
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    fetchBillData();
    fetchReservationData();
  }, [fromDate, toDate]);

  const totalReservationPayment = reservationData.reduce((total, reservation) => total + reservation.totalPayment, 0);

  const totalFoodPayment = reservationData?.reduce((total, reservation) => {
    if (reservation.foodItems && reservation.foodItems.length > 0) {
      return total + reservation.foodItems.reduce((foodTotal, food) => foodTotal + food.price * food.quantity, 0);
    } else {
      return total;
    }
  }, 0);

  const totalSeparateFoodPayment = separateFoodData?.reduce((total, item) => total + (item.totalAmount || 0), 0);

  // Prepare data for the income pie chart
  const pieChartIncomeData = [
    { name: 'Reservation', value: totalReservationPayment },
    { name: 'Food', value: totalSeparateFoodPayment + totalFoodPayment },
  ];

  // Format the value with currency for CSV export
  const formatCurrency = (value) => {
    let currency = currencies.find((item) => item.symbol == hotel?.currencyCode)
    let currencyCode = '-';
    if (currency) {
      currencyCode = currency.code
    }
    return `${currencyCode} ${value.toFixed(2)}`; // Assuming two decimal places for the currency amount
  };

  const exportToCSV = () => {
    const csvData = pieChartIncomeData
      .map((item) => `"${item.name}","${formatCurrency(item.value)}"`) // Ensure that commas and symbols are handled as strings
      .join('\n');

    // Create a Blob object with the CSV data and UTF-8 encoding
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'income_data.csv');
  };

  const printPieChart = () => {
    const chartDiv = document.getElementById('pie-chart-div');
    if (chartDiv) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write('<html><head><title>PieChart Print</title></head><body>');
      printWindow.document.write(chartDiv.innerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Calculate total rooms and occupancy percentage
  const totalRooms = hotel.totalRooms || 0; // Assuming hotel.totalRooms gives the total number of rooms
  const totalReservedRooms = reservationData.length; // Assuming each reservation corresponds to a reserved room

  // Calculate occupancy percentage
  const occupancyPercentage = totalRooms > 0 ? (totalReservedRooms / totalRooms) * 100 : 0;

  return (
    <Container>
      <Stack direction="row" alignItems="center" mb={5} justifyContent={'space-between'}>
        <Typography variant="h4">General Reports</Typography>
        <Typography variant="h5">{`Overall Occupancy: ${occupancyPercentage.toFixed(2)}%`}</Typography>
      </Stack>
      <Grid container spacing={gridSpacing}>
        <Grid item xs={12}>
          <Grid container spacing={gridSpacing}>
            {/* <Grid item xs={12} md={6}>
              <Item>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      From
                    </Typography>
                    <TextField
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      sx={{ marginBottom: 2 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body1" sx={{}}>
                      To
                    </Typography>
                    <TextField
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </Box>
                  <Button variant="contained" onClick={handleClick} sx={{ ml: 2 }}>
                    Export / Print
                  </Button>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                    <MenuItem onClick={exportToCSV}>Export to CSV</MenuItem>
                    <MenuItem onClick={printPieChart}>Print</MenuItem>
                  </Menu>
                </Box>

                <Box id="pie-chart-div">
                  <PieChart width={500} height={340}>
                    <Pie
                      data={pieChartIncomeData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      label
                    >
                      {pieChartIncomeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getRandomColor()} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column' }}>
                    <Typography>Food Income ( {hotel?.currencyCode} {totalSeparateFoodPayment + totalFoodPayment} )</Typography>
                    <Typography>Reservation Income ( {hotel?.currencyCode} {totalReservationPayment} )</Typography>
                  </Box>
                </Box>
              </Item>
            </Grid> */}
            <Grid item xs={12} md={6}>
              <Item>
                <Typography variant="h5">Reserved Rooms</Typography>
                <ReservedRoomView reservationData={reservationData} />
              </Item>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export default GeneralReports;
